import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    'answer', 'progress', 'progressBar',
    'placeholder', 'resultsContainer', 'scoreContainer',
    'score', 'grade', 'gradeBadge',
    'durationScore', 'durationBar',
    'efficiencyScore', 'efficiencyBar',
    'hygieneScore', 'hygieneBar',
    'functionScore', 'functionBar',
    'tips', 'insight'
  ]

  connect() {
    this.updateProgress()
  }

  calculate() {
    const answers = this.getAnsweredQuestions()
    this.updateProgress()

    if (Object.keys(answers).length === 10) {
      this.showResults(answers)
    }
  }

  getAnsweredQuestions() {
    const questions = {}
    this.answerTargets.forEach((input) => {
      if (input.checked) {
        questions[input.name] = parseInt(input.value, 10)
      }
    })
    return questions
  }

  updateProgress() {
    const answers = this.getAnsweredQuestions()
    const count = Object.keys(answers).length
    const percentage = (count / 10) * 100

    this.progressTarget.textContent = count
    this.progressBarTarget.style.width = `${percentage}%`
  }

  showResults(answers) {
    // Calculate category scores
    const categoryScores = this.calculateCategoryScores(answers)
    const totalScore = this.calculateTotalScore(categoryScores)
    const grade = this.determineGrade(totalScore)

    // Update main score display
    this.scoreTarget.textContent = totalScore
    this.gradeTarget.textContent = grade.name
    this.updateGradeBadge(grade)
    this.updateScoreContainer(grade)

    // Update category displays
    this.durationScoreTarget.textContent = `${categoryScores.duration}%`
    this.durationBarTarget.style.width = `${categoryScores.duration}%`
    this.efficiencyScoreTarget.textContent = `${categoryScores.efficiency}%`
    this.efficiencyBarTarget.style.width = `${categoryScores.efficiency}%`
    this.hygieneScoreTarget.textContent = `${categoryScores.hygiene}%`
    this.hygieneBarTarget.style.width = `${categoryScores.hygiene}%`
    this.functionScoreTarget.textContent = `${categoryScores.function}%`
    this.functionBarTarget.style.width = `${categoryScores.function}%`

    // Generate and display tips
    const tips = this.generateTips(answers, categoryScores)
    this.tipsTarget.innerHTML = tips
      .map((tip) => `<li class="flex items-start gap-2">` +
        `<span class="text-gray-400">></span><span>${tip}</span></li>`)
      .join('')

    // Update insight
    this.updateInsight(categoryScores, totalScore)

    // Show results, hide placeholder
    this.placeholderTarget.classList.add('hidden')
    this.resultsContainerTarget.classList.remove('hidden')

    // Scroll to results on mobile
    if (window.innerWidth < 1024) {
      this.resultsContainerTarget.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  calculateCategoryScores(answers) {
    // Duration & Timing (Q1: duration, Q5: consistency)
    // Q1: 3-4 = optimal (7-9 hours), 2 = okay, 0-1 and 5 = suboptimal
    const durationPoints = { 0: 0, 1: 40, 2: 70, 3: 100, 4: 100, 5: 80 }
    const consistencyPoints = { 0: 0, 1: 33, 2: 66, 3: 100 }
    const duration = Math.round((durationPoints[answers.q1] + consistencyPoints[answers.q5]) / 2)

    // Sleep Efficiency (Q2: latency, Q3: awakenings)
    const latencyPoints = { 0: 100, 1: 75, 2: 40, 3: 0 }
    const awakeningPoints = { 0: 100, 1: 75, 2: 40, 3: 0 }
    const efficiency = Math.round((latencyPoints[answers.q2] + awakeningPoints[answers.q3]) / 2)

    // Sleep Hygiene (Q6: screens, Q7: caffeine, Q8: environment)
    const screenPoints = { 0: 0, 1: 33, 2: 66, 3: 100 }
    const caffeinePoints = { 0: 0, 1: 33, 2: 66, 3: 100 }
    const environmentPoints = { 0: 0, 1: 33, 2: 66, 3: 100 }
    const hygiene = Math.round((screenPoints[answers.q6] + caffeinePoints[answers.q7] + environmentPoints[answers.q8]) / 3)

    // Daytime Function (Q4: refreshed, Q9: sleepiness, Q10: satisfaction)
    const refreshedPoints = { 0: 0, 1: 25, 2: 50, 3: 75, 4: 100 }
    const sleepinessPoints = { 0: 0, 1: 25, 2: 50, 3: 75, 4: 100 }
    const satisfactionPoints = { 0: 0, 1: 25, 2: 50, 3: 75, 4: 100 }
    const daytimeFunction = Math.round((refreshedPoints[answers.q4] + sleepinessPoints[answers.q9] + satisfactionPoints[answers.q10]) / 3)

    return {
      duration,
      efficiency,
      hygiene,
      function: daytimeFunction
    }
  }

  calculateTotalScore(categoryScores) {
    const weights = { duration: 0.25, efficiency: 0.30, hygiene: 0.20, function: 0.25 }
    return Math.round(categoryScores.duration * weights.duration +
      categoryScores.efficiency * weights.efficiency +
      categoryScores.hygiene * weights.hygiene +
      categoryScores.function * weights.function)
  }

  determineGrade(score) {
    if (score >= 85) {
      return { name: 'EXCELLENT', color: 'border-green-500 text-green-700', bg: 'border-green-500' }
    }
    if (score >= 70) {
      return { name: 'GOOD', color: 'border-blue-500 text-blue-700', bg: 'border-blue-500' }
    }
    if (score >= 50) {
      return { name: 'FAIR', color: 'border-yellow-500 text-yellow-700', bg: 'border-yellow-500' }
    }
    return { name: 'POOR', color: 'border-red-500 text-red-700', bg: 'border-red-500' }
  }

  updateGradeBadge(grade) {
    this.gradeBadgeTarget.className = `px-4 py-2 border-2 font-medium text-sm ${grade.color}`
  }

  updateScoreContainer(grade) {
    this.scoreContainerTarget.className = `p-8 bg-white border-2 text-center ${grade.bg}`
  }

  generateTips(answers, _categoryScores) {
    const tips = []

    // Duration tips
    if (answers.q1 <= 1) {
      tips.push('Prioritize getting 7-8 hours of sleep. This is critical for cognitive function and recovery.')
    }
    if (answers.q5 <= 1) {
      tips.push('Maintain a consistent sleep schedule, even on weekends. Your body thrives on predictability.')
    }

    // Efficiency tips
    if (answers.q2 >= 2) {
      tips.push('Try relaxation techniques like 4-7-8 breathing or progressive muscle relaxation to fall asleep faster.')
    }
    if (answers.q3 >= 2) {
      tips.push('Frequent awakenings may indicate sleep environment issues or underlying conditions to address.')
    }

    // Hygiene tips
    if (answers.q6 <= 1) {
      tips.push('Reduce screen exposure 1+ hour before bed. Blue light suppresses melatonin production.')
    }
    if (answers.q7 <= 1) {
      tips.push('Cut off caffeine at least 8 hours before bedtime. It has a half-life of 5-6 hours.')
    }
    if (answers.q8 <= 1) {
      tips.push('Optimize your sleep environment: blackout curtains, white noise, and keep temperature at 60-67F (15-19C).')
    }

    // Function tips
    if (answers.q4 <= 1) {
      tips.push('Not feeling refreshed may indicate poor sleep quality. Consider tracking your sleep stages.')
    }
    if (answers.q9 <= 1) {
      tips.push('Excessive daytime sleepiness could signal sleep debt or a sleep disorder. Consider evaluation.')
    }

    // If all good
    if (tips.length === 0) {
      tips.push('Your sleep habits are excellent! Maintain your current routine for optimal health.')
    }

    return tips.slice(0, 5) // Limit to 5 tips
  }

  updateInsight(categoryScores, _totalScore) {
    const categories = [
      { name: 'sleep duration and timing', score: categoryScores.duration },
      { name: 'sleep efficiency', score: categoryScores.efficiency },
      { name: 'sleep hygiene habits', score: categoryScores.hygiene },
      { name: 'daytime function', score: categoryScores.function }
    ]

    const weakest = categories.reduce((min, cat) => (cat.score < min.score ? cat : min))
    const strongest = categories.reduce((max, cat) => (cat.score > max.score ? cat : max))

    let insight
    if (weakest.score < 50) {
      insight = `Your ${weakest.name} needs the most attention. ` +
        `Improving this area could significantly boost your overall sleep quality. ` +
        `Your ${strongest.name} is your strongest area.`
    } else if (weakest.score < 70) {
      insight = `Good sleep foundation! Focus on optimizing ${weakest.name} for even better results. ` +
        `Your ${strongest.name} is already excellent.`
    } else {
      insight = 'Excellent sleep quality across all categories! ' +
        'Your habits are well-optimized for restorative sleep. Keep up the great work.'
    }

    this.insightTarget.textContent = insight
  }
}
